import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import HomeIcon from "@mui/icons-material/Home";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MapIcon from "@mui/icons-material/Map";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import StyleIcon from "@mui/icons-material/Style";
import Box from "@mui/material/Box";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import useOpenLayersMap from "../hooks/useOpenLayersMap";
import MapFullScreen from "../map/mapFullScreen";
import MapTileDropdown from "../map/mapTileDropdown";
import AssetCards from "./assetCards";
import AssetCreator from "./assetCreator";
import AssetInfo from "./assetInfo";

import { Fab, Typography, useMediaQuery } from "@mui/material";
import { useUserAuth } from "../contexts/authContext";
import { useCuas } from "../contexts/cuasContext";
import { useMap } from "../contexts/mapContext";
import { GetFeature, getHistLineCoordinatesFromTrack, getPredLineCoordinatesFromTrackAndSettings, shouldHideAsd, STYLE_VOLUME } from "../map/mapUtil";
import { CreateAsset, CreateEntity, DeleteCounterUas, DeleteCounterUasFromAssetMonitor, DisplayHighestAlert } from "./assetUtil";
import { LineString } from "ol/geom";
import { Feature } from "ol";
import { Stroke } from "ol/style";

export default function AssetMap() {
    const MAP_ID = "asset-map";
    const map = useOpenLayersMap(MAP_ID);
    const { assets } = useMap();
    const { user, socket, userMapSettings } = useUserAuth();
    const { startAssetMonitoring, stopAssetMonitoring } = useCuas();

    const settingsRef = useRef(userMapSettings);
    const entitiesRef = useRef(new VectorSource());
    const assetsRef = useRef(new VectorSource());
    const mapLocationRef = useRef(null);

    const [assetCreatorOpen, setAssetCreatorOpen] = useState(false);
    const [assetCardsOpen, setAssetCardsOpen] = useState(false);
    const [editFeature, setEditFeature] = useState(null);
    const [editFeatureEnabledId, setEditFeatureEnabledId] = useState(null); // Contains the ID of an edit feature that was previously enabled.

    const [mapTileDropdownOpen, setMapTileDropdownOpen] = useState(false);
    const [mapTileDropdownAnchorEl, setMapTileDropdownAnchorEl] = useState(null);

    const [mapContainer, setMapContainer] = useState(null);
    const mapContainerRef = useRef(null);

    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const isDesktop = useMediaQuery("(min-width:900px)");

    useEffect(() => {
        if (!map) {
            return;
        }

        map.addLayer(new VectorLayer({ source: entitiesRef.current, zIndex: 10, properties: { name: "asd", selectable: true } }));
        map.addLayer(new VectorLayer({ source: assetsRef.current, style: STYLE_VOLUME, zIndex: 1, properties: { name: "assets", selectable: true } }));
        map.addLayer(new VectorLayer({ source: new VectorSource(), style: STYLE_VOLUME, zIndex: 1, properties: { name: "assetMonitors" } }));

        map.on("pointermove", ({ originalEvent }) => {
            const coordinates = map.getEventCoordinate(originalEvent);
            if (mapLocationRef.current) {
                mapLocationRef.current.innerHTML = `( ${coordinates[1]}, ${coordinates[0]} )`;
            }
        });

        socket.emit("mapOpen");
        socket.emit("cuasOpen");
        socket.on("asd", (asdMessages) => asdMessages.forEach((asd) => processASD(asd)));
        socket.on("cuas_asset_alerts", (alert) => processAssetAlert(alert));
        const expireInterval = setInterval(() => removeExpiredEntities(), 1000);

        return () => {
            socket.emit("mapClose");
            socket.emit("cuasClose");
            socket.off("asd");
            socket.off("cuas_asset_alerts");
            clearInterval(expireInterval);
        };
    }, [map]);

    useEffect(() => {
        assetsRef.current.clear();
        const GeoJson = new GeoJSON();
        for (const asset of assets) {
            if (asset.uuid === editFeature?.getId()) {
                continue;
            }

            const assetFeature = assetsRef.current.getFeatureById(asset.uuid);
            if (assetFeature) {
                assetFeature.setGeometry(GeoJson.readGeometry(asset.geometry));
                assetFeature.set("asset", asset);
            } else {
                assetsRef.current.addFeature(CreateAsset(asset));
            }

            if (asset.active_monitoring) {
                startAssetMonitoring(map, asset.uuid);
            } else {
                stopAssetMonitoring(map, asset.uuid);
            }

            // Handles re-enabling assets on edit submission.
            if (editFeatureEnabledId && editFeatureEnabledId === asset.uuid) {
                startAssetMonitoring(map, asset.uuid);
                setEditFeatureEnabledId(null);
            }
        }
    }, [map, assets]);

    useEffect(() => {
        if (assetCreatorOpen) {
            // Close asset cards on mobile to allow more room.
            if (!isDesktop) {
                setAssetCardsOpen(false);
            }
            return;
        }

        setEditFeature(null);
        setMobileDrawerOpen(false);

        // Handles re-enabling assets on edit cancel.
        if (GetFeature(map, "assets", editFeatureEnabledId)) {
            startAssetMonitoring(map, editFeatureEnabledId);
            setEditFeatureEnabledId(null);
        }
    }, [assetCreatorOpen]);

    useEffect(() => {
        if (editFeature) {
            setEditFeatureEnabledId(editFeature.get("asset").active_monitoring ? editFeature.getId() : null);
            stopAssetMonitoring(map, editFeature.getId());
            setAssetCreatorOpen(true);
        }
    }, [editFeature]);

    const processASD = (asd) => {
        if (shouldHideAsd(asd, user)) {
            return;
        }

        const entitySettings = settingsRef.current.source_type_settings.find((sources) => sources.source_type === asd.source_type);
        const entityData = {
            asd,
            coordinates: [asd.lon_deg, asd.lat_deg],
            rotation: -(asd.rotation * (Math.PI / 180)),
            sourceType: asd.source_type,
            emitterType: asd.adsb ? asd.adsb.emitter_cat : 0,
            model: entitySettings.icon,
            color: entitySettings.color,
            size: settingsRef.current.entity_size + 0.2
        };

        const entity = entitiesRef.current.getFeatureById(asd.id);
        if (!entity) {
            const newEntity = CreateEntity(entityData);
            entitiesRef.current.addFeature(newEntity);
            handleHistoryAndPredictionLines(asd, entityData, newEntity);
            return;
        }

        const style = entity.get("style");
        style.getImage().setRotation(entityData.rotation);
        entity.set("style", style);
        entity.set("asd", asd);
        entity.set("update_timestamp", new Date(asd.timestamp));
        entity.getGeometry().setCoordinates(entityData.coordinates);

        // The ASD is only visible if a counter UAS alert is active for it.
        if (entity.get("cuas")) {
            entity.setStyle([entity.get("style"), entity.get("cuasStyle")]);
        } else {
            entity.setStyle(new Style(null));
        }

        handleHistoryAndPredictionLines(asd, entityData);
    };

    const handleHistoryAndPredictionLines = (asd, entityData, asdFeature) => {
        const lines = [
            { id: `${asd.id}_pl`, coordinates: getPredLineCoordinatesFromTrackAndSettings(asd, userMapSettings), visible: userMapSettings.pred_line },
            { id: `${asd.id}_hl`, coordinates: getHistLineCoordinatesFromTrack(asd), visible: userMapSettings.hist_line }
        ];
        for (const { id, coordinates, visible } of lines) {
            const line = entitiesRef.current.getFeatureById(id);
            if (line) {
                line.getGeometry().setCoordinates(coordinates);
                line.setProperties({
                    asd,
                    update_timestamp: asd.timestamp,
                    style: id.endsWith("_pl")
                        ? new Style({ stroke: new Stroke({ color: entityData.color, width: 2 }) })
                        : new Style({ stroke: new Stroke({ color: entityData.color, width: 2, lineDash: [5, 5] }) })
                });
                continue;
            }

            const newLine = new Feature(new LineString(coordinates));
            newLine.setId(id);
            newLine.setProperties({
                name: asd.label,
                asd,
                update_timestamp: asd.timestamp,
                style: id.endsWith("_pl")
                    ? new Style({ stroke: new Stroke({ color: entityData.color, width: 2 }) })
                    : new Style({ stroke: new Stroke({ color: entityData.color, width: 2, lineDash: [5, 5] }) })
            });
            newLine.setStyle(new Style(null));
            entitiesRef.current.addFeature(newLine);

            // Handle visibility of history / prediction lines depending on entity visibility.
            asdFeature.on("propertychange", () => {
                if (asdFeature.get("cuas") && visible) {
                    newLine.setStyle(newLine.get("style"));
                } else {
                    newLine.setStyle(new Style(null));
                }
            });
        }
    };

    const processAssetAlert = (alert) => {
        const asdEntity = entitiesRef.current.getFeatureById(alert.asd_uuid);
        const assetMonitor = GetFeature(map, "assetMonitors", `${alert.asset_id}-monitor`);
        const assetMonitors = asdEntity && asdEntity.get("monitors") ? asdEntity.get("monitors") : [];

        // Delete the old counter UAS and only update it if the ASD is available and the alert is active.
        DeleteCounterUasFromAssetMonitor(asdEntity, assetMonitor);
        if (!asdEntity || !assetMonitor || alert.state === "EXPIRED") {
            DisplayHighestAlert(asdEntity);
            return;
        }

        // Include the new asset monitor if it has never processed this ASD.
        if (!assetMonitors.includes(assetMonitor)) {
            assetMonitors.push(assetMonitor);
        }

        asdEntity.set("cuas", true);
        asdEntity.set("cuas_update_timestamp", new Date(alert.timestamp));
        asdEntity.set("monitors", assetMonitors);

        // Set the counter UAS on the monitor to be displayed in the info card.
        const counterUAS = assetMonitor.get("cuas") ? [...assetMonitor.get("cuas")] : [];
        counterUAS.push(asdEntity);
        assetMonitor.set("cuas", counterUAS);

        // Set the number of alerts on the monitor to be displayed in the info card / asset card.
        assetMonitor.set("highAlerts", assetMonitor.get("highAlerts") + (alert.risk_val === "HIGH" ? 1 : 0));
        assetMonitor.set("mediumAlerts", assetMonitor.get("mediumAlerts") + (alert.risk_val === "MEDIUM" ? 1 : 0));
        assetMonitor.set("noRiskAlerts", assetMonitor.get("noRiskAlerts") + (alert.risk_val === "NO_RISK" ? 1 : 0));

        // Set the alert messages to display the highest alert if multiple alerts are sent for the same ASD.
        const alerts = assetMonitor.get("alerts") || {};
        alerts[asdEntity.getId()] = alert;
        assetMonitor.set("alerts", alerts);
        DisplayHighestAlert(asdEntity);
    };

    const removeExpiredEntities = () => {
        if (!entitiesRef.current) {
            return;
        }

        const currentTime = new Date().getTime();
        entitiesRef.current.forEachFeature((entity) => {
            const asdExpireTime = new Date(entity.get("update_timestamp")).getTime();
            const cuasExpireTime = new Date(entity.get("cuas_update_timestamp")).getTime();

            // Remove entities if they are old or the cUAS alert hasn't been updated in 10 seconds.
            if (currentTime - asdExpireTime > 10000 || currentTime - cuasExpireTime > 10000) {
                DeleteCounterUas(entity);
                entitiesRef.current.removeFeature(entity);
            }
        });
    };

    const handleHomeButtonClick = () => {
        if (map && map.getView()) {
            map.getView().setCenter([userMapSettings.longitude, userMapSettings.latitude]);
            map.getView().setZoom(12);
        }
    };

    const handleMapTileDropdownButtonClick = (e) => {
        setMapTileDropdownOpen((prev) => !prev);
        setMapTileDropdownAnchorEl(e.currentTarget);
    };

    const handleFullScreenButtonClick = () => {
        try {
            // Start fullscreen.
            if (!mapContainer) {
                mapContainerRef.current.requestFullscreen();
                setMapContainer(mapContainerRef.current);
                return;
            }

            // Stop fullscreen.
            setMapContainer(null);
            if (document.fullscreenElement) {
                document.exitFullscreen();
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Box ref={mapContainerRef} style={{ position: "relative", width: "100%", height: "100%", display: "flex" }}>
            {/* Asset creation menu. */}
            {assetCreatorOpen && (
                <AssetCreator
                    map={map}
                    editFeature={editFeature}
                    setEditFeature={setEditFeature}
                    setAssetCreatorOpen={setAssetCreatorOpen}
                    mobileDrawerOpen={mobileDrawerOpen}
                    setMobileDrawerOpen={setMobileDrawerOpen}
                />
            )}

            {/* Map being rendered. */}
            <Box sx={{ flex: 1, position: "relative" }}>
                <MapFullScreen
                    map_id={MAP_ID}
                    map={map}
                    buttons={[
                        {
                            id: "viewHome",
                            name: "View Home",
                            icon: <HomeIcon />,
                            onClick: handleHomeButtonClick,
                            visible: isDesktop || !mobileDrawerOpen
                        },
                        {
                            id: "createAsset",
                            name: "Create Asset",
                            icon: <EditIcon />,
                            onClick: () => setAssetCreatorOpen(true),
                            visible: isDesktop || !mobileDrawerOpen
                        },
                        {
                            id: "assetCards",
                            name: "Asset Cards",
                            icon: <StyleIcon />,
                            onClick: () => setAssetCardsOpen(!assetCardsOpen),
                            visible: isDesktop || (!assetCreatorOpen && !mobileDrawerOpen)
                        },
                        {
                            name: "Map Tiles",
                            icon: <MapIcon />,
                            onClick: handleMapTileDropdownButtonClick,
                            visible: isDesktop || !mobileDrawerOpen
                        },
                        {
                            name: mapContainer ? "Exit Fullscreen" : "Enter Fullscreen",
                            icon: mapContainer ? <CloseIcon /> : <OpenInFullIcon />,
                            onClick: handleFullScreenButtonClick,
                            visible: isDesktop || !mobileDrawerOpen
                        }
                    ]}
                >
                    <AssetInfo map={map} />
                </MapFullScreen>

                {/* Mobile drawer open button. */}
                {assetCreatorOpen && (
                    <Fab
                        size="medium"
                        color="primary"
                        onClick={() => setMobileDrawerOpen(true)}
                        sx={{ display: isDesktop || mobileDrawerOpen ? "none" : "block", position: "absolute", bottom: 8, left: 8 }}
                    >
                        <KeyboardArrowRightIcon />
                    </Fab>
                )}

                {/* (Lat, Lon) location at the bottom right. */}
                {!mobileDrawerOpen && (
                    <Typography
                        ref={mapLocationRef}
                        variant="caption"
                        sx={{ position: "absolute", right: "0.5em", bottom: ".5em", zIndex: 1, textShadow: "0 0 10px #000" }}
                    />
                )}
            </Box>

            {/* Asset cards menu. */}
            {assetCardsOpen && (
                <AssetCards
                    map={map}
                    editFeature={editFeature}
                    setEditFeature={setEditFeature}
                    assetCardsOpen={assetCardsOpen}
                    setAssetCardsOpen={setAssetCardsOpen}
                />
            )}

            {/* Map tiles dropdown. */}
            <MapTileDropdown
                mapTileDropdownOpen={mapTileDropdownOpen}
                setMapTileDropdownOpen={setMapTileDropdownOpen}
                mapTileDropdownAnchorEl={mapTileDropdownAnchorEl}
                mapContainer={mapContainer}
            />
        </Box>
    );
}
